import { useState } from "react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconFolder,
  IconFolderPlus,
} from "@tabler/icons-react";
import {
  useCreateFolderMutation,
  useFolderContentsQuery,
} from "@/lib/queries/folder-queries";
import { Button } from "@/lib/components/Button/Button";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import NameDialog from "./NameDialog";
import { cn } from "@/lib/utils";
import { MAX_FOLDER_DEPTH } from "@repo/api/folder";
import Toast from "@/lib/components/Toast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";

const MAX_FOLDER_DEPTH_MESSAGE = `You can only nest folders up to ${MAX_FOLDER_DEPTH} levels deep.`;

type Props = {
  teamExternalId: string;
  excludeFolderExternalId?: string;
  currentLocationExternalId: string | null;
  allowRoot: boolean;
  placedFolderDepth?: number;
  isMoving: boolean;
  onSelect: (folderExternalId: string | null) => void;
};

export default function FolderPicker({
  teamExternalId,
  excludeFolderExternalId,
  currentLocationExternalId,
  allowRoot,
  placedFolderDepth = 0,
  isMoving,
  onSelect,
}: Props) {
  const [browseFolderExternalId, setBrowseFolderExternalId] = useState<
    string | undefined
  >(currentLocationExternalId ?? undefined);

  const { data, isPending, isError } = useFolderContentsQuery(
    teamExternalId,
    browseFolderExternalId,
  );

  const currentFolder = data?.folder ?? null;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const createFolderMutation = useCreateFolderMutation({
    teamExternalId,
    onSuccess: () => setIsCreateOpen(false),
  });

  const folders =
    data?.folders.filter(
      (folder) => folder.externalId !== excludeFolderExternalId,
    ) ?? [];

  const levelExternalId = browseFolderExternalId ?? null;
  const browseLevel = data?.breadcrumb.length ?? 0;
  const canCreateHere = browseLevel < MAX_FOLDER_DEPTH;
  const isValidTarget =
    (levelExternalId !== null || allowRoot) &&
    levelExternalId !== currentLocationExternalId;
  const withinMaxDepth = browseLevel + placedFolderDepth <= MAX_FOLDER_DEPTH;

  const handleCreate = () => {
    if (!canCreateHere) {
      Toast.info(MAX_FOLDER_DEPTH_MESSAGE);
      return;
    }
    setIsCreateOpen(true);
  };

  const handleMoveHere = () => {
    if (!withinMaxDepth) {
      Toast.info(MAX_FOLDER_DEPTH_MESSAGE);
      return;
    }
    onSelect(levelExternalId);
  };

  return (
    <div className="flex max-h-80 flex-col">
      <div className="flex min-h-12 items-center gap-1 border-b border-border px-3">
        {browseFolderExternalId !== undefined && (
          <Button
            type="button"
            variant="ghost"
            size="iconXxs"
            disabled={isPending}
            onClick={() =>
              setBrowseFolderExternalId(
                currentFolder?.parentFolderExternalId ?? undefined,
              )
            }
            aria-label="Back"
            className="-ml-1"
          >
            <IconChevronLeft />
          </Button>
        )}
        <p
          className={cn(
            "line-clamp-1 text-sm font-semibold",
            browseFolderExternalId === undefined && "ml-1",
          )}
        >
          {currentFolder?.name ?? "Videos"}
        </p>
      </div>
      <div className="flex min-h-32 flex-1 flex-col overflow-y-auto px-2 py-1.5">
        {isPending && (
          <div className="flex flex-1 items-center justify-center py-4">
            <LoadingSpinner size={20} />
          </div>
        )}
        {isError && (
          <div className="flex flex-1 items-center justify-center py-3">
            <p className="text-sm text-error">Failed to load folders</p>
          </div>
        )}
        {folders.map((folder) => (
          <button
            key={folder.externalId}
            type="button"
            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-background-secondary disabled:cursor-default disabled:opacity-50"
            disabled={isMoving}
            onClick={() => setBrowseFolderExternalId(folder.externalId)}
          >
            <IconFolder size={16} className="shrink-0 text-text-secondary" />
            <span className="line-clamp-1 min-w-0 flex-1">{folder.name}</span>
            <IconChevronRight
              size={16}
              className="shrink-0 text-text-secondary"
            />
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-border p-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="iconXs"
              onClick={handleCreate}
              aria-label="New folder"
            >
              <IconFolderPlus size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Create a new folder</TooltipContent>
        </Tooltip>
        <Button
          type="button"
          variant="default"
          size="xs"
          disabled={!isValidTarget}
          isLoading={isMoving}
          onClick={handleMoveHere}
        >
          Move here
        </Button>
      </div>
      <NameDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        title="New folder"
        description="Organize your videos by grouping them into folders."
        placeholder="Folder name"
        submitLabel="Create"
        isSubmitting={createFolderMutation.isPending}
        onSubmit={(name) =>
          createFolderMutation.mutate({
            name,
            parentFolderExternalId: levelExternalId,
          })
        }
      />
    </div>
  );
}
